import { Button } from "@/components/primitives";
import { Edit, Trash2 } from "lucide-react";
import { formatDate, formatRelativeDate } from "@/lib/utils";
import { getFeelingArrows } from "@/utils/feeling-utils";
import type { DailyCheckIn } from "../types";
import { CheckInReflection } from "./CheckInReflection";

interface CheckInCardProps {
	checkIn: DailyCheckIn;
	onEdit: (checkIn: DailyCheckIn, event: React.MouseEvent) => void;
	onDelete: (checkInId: string, event: React.MouseEvent) => void;
	refCallback?: (el: HTMLDivElement | null) => void;
}

export const CheckInCard = ({
	checkIn,
	onEdit,
	onDelete,
	refCallback,
}: CheckInCardProps) => {
	const feelingArrows = getFeelingArrows(checkIn.general_feeling);

	return (
		<div
			ref={refCallback}
			className="p-4 sm:p-6 border-2 rounded-lg bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm shadow-md hover:shadow-xl transition-all duration-200 border-border/50 hover:border-primary/50"
		>
			{/* Mobile Layout */}
			<div className="block sm:hidden space-y-4">
				<div className="flex items-start justify-between gap-3">
					<div className="flex-1">
						<div className="flex items-center gap-2 mb-1">
							<div className="text-lg">{feelingArrows}</div>
							<div className="text-base font-semibold text-foreground">
								{formatDate(checkIn.created_at)}
							</div>
						</div>
						<div className="text-xs text-muted-foreground">
							{formatRelativeDate(checkIn.created_at)}
						</div>
					</div>
					<div className="flex items-center gap-1">
						<Button
							variant="ghost"
							size="sm"
							className="h-8 w-8 p-0 text-muted-foreground hover:text-primary"
							onClick={(e) => onEdit(checkIn, e)}
						>
							<Edit className="h-4 w-4" />
						</Button>
						<Button
							variant="ghost"
							size="sm"
							className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
							onClick={(e) => onDelete(checkIn.id, e)}
						>
							<Trash2 className="h-4 w-4" />
						</Button>
					</div>
				</div>

				<div className="pl-7">
					{checkIn.day_description ? (
						<p className="text-sm text-foreground leading-relaxed">
							{checkIn.day_description}
						</p>
					) : (
						<p className="text-sm text-muted-foreground italic">
							No description provided
						</p>
					)}
				</div>

				<div className="pl-7">
					<CheckInReflection checkIn={checkIn} isMobile />
				</div>
			</div>

			{/* Desktop Layout */}
			<div className="hidden sm:grid grid-cols-12 gap-4 items-start">
				<div className="col-span-1 flex items-center justify-center">
					<div className="text-2xl">{feelingArrows}</div>
				</div>

				<div className="col-span-2">
					<div className="text-base font-semibold text-foreground">
						{formatDate(checkIn.created_at)}
					</div>
					<div className="text-xs text-muted-foreground mt-1">
						{formatRelativeDate(checkIn.created_at)}
					</div>
				</div>

				<div className="col-span-5">
					{checkIn.day_description ? (
						<p className="text-sm text-foreground leading-relaxed">
							{checkIn.day_description}
						</p>
					) : (
						<p className="text-sm text-muted-foreground italic">
							No description provided
						</p>
					)}
				</div>

				<div className="col-span-3">
					<CheckInReflection checkIn={checkIn} />
				</div>

				<div className="col-span-1 flex items-center justify-center gap-1">
					<Button
						variant="ghost"
						size="sm"
						className="h-8 w-8 p-0 text-muted-foreground hover:text-primary"
						onClick={(e) => onEdit(checkIn, e)}
					>
						<Edit className="h-4 w-4" />
					</Button>
					<Button
						variant="ghost"
						size="sm"
						className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
						onClick={(e) => onDelete(checkIn.id, e)}
					>
						<Trash2 className="h-4 w-4" />
					</Button>
				</div>
			</div>
		</div>
	);
};
